import { AnyRecord } from '@lighthouse/core'
import type { UploadOptions } from '@rpldy/uploady'
import { mergeAttributes, Node } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'

import { getImageFullUrlInApplication } from '../../../utils'
import type { TokenRefreshManager } from '../../../utils/tokenRefreshManager'
import { ParseVideoPlugin, UPLOAD_VIDEO_PLUGIN_KEY } from './plugin'
import { ResizeableVideo } from './ResizeableVideo'

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        customVideo: {
            insertVideo: (files: File[]) => ReturnType
            insertVideoByUrl: (url: string) => ReturnType
        }
    }
}

export interface UploadVideoOptions {
    appId: string
    uploadOptions: UploadOptions
    tokenRefreshManager?: TokenRefreshManager
    HTMLAttributes?: AnyRecord
    /** 最大上传大小（字节），默认 100MB */
    maxSize?: number
}

export const UploadVideo = Node.create<UploadVideoOptions>({
    name: 'video',
    group: 'block',
    draggable: true,
    atom: true,

    addOptions() {
        return {
            appId: '',
            uploadOptions: {
                destination: {}
            },
            HTMLAttributes: {},
            maxSize: 100 * 1024 * 1024 // 100MB default
        }
    },

    addAttributes() {
        return {
            src: {
                default: null
            },
            width: {
                default: '100%',
                parseHTML(element) {
                    return element.style.width || element.getAttribute('width')
                },
                renderHTML(attributes) {
                    return {
                        width: attributes.width
                    }
                }
            },
            height: {
                default: null,
                parseHTML(element) {
                    return element.style.height || element.getAttribute('height')
                },
                renderHTML(attributes) {
                    return {
                        height: attributes.height
                    }
                }
            },
            controls: {
                default: true
            }
        }
    },

    parseHTML() {
        return [
            {
                tag: 'video'
            }
        ]
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'video',
            mergeAttributes(this.options.HTMLAttributes || {}, {
                ...HTMLAttributes,
                src: getImageFullUrlInApplication(this.options.appId, this.options.HTMLAttributes?.src),
                preload: 'none'
            })
        ]
    },

    addCommands() {
        return {
            insertVideo:
                files =>
                ({ view }) => {
                    const { tr } = view.state
                    tr.setMeta(UPLOAD_VIDEO_PLUGIN_KEY, files)
                    view.dispatch(tr)
                    return true
                },
            insertVideoByUrl:
                url =>
                ({ commands }) =>
                    commands.insertContent({ type: this.name, attrs: { src: url } })
        }
    },

    addProseMirrorPlugins() {
        return this.editor.isEditable
            ? [
                  ParseVideoPlugin({
                      appId: this.options.appId,
                      uploadOptions: this.options.uploadOptions,
                      tokenRefreshManager: this.options.tokenRefreshManager,
                      maxSize: this.options.maxSize
                  })
              ]
            : []
    },

    addNodeView(..._args: unknown[]) {
        return ReactNodeViewRenderer(ResizeableVideo)
    }
})
